import { useCallback, useEffect, useMemo, useRef, useState, type ChangeEvent } from "react";
import { fetchComponentsFromAPI } from "../../api/componentsApi";
import { aclAPI } from "../../api/aclApi";
import {
  PublicJsonFeed,
  PublicJsonFeedSource,
  createJsonFeed,
  DEFAULT_PUBLIC_JSON_FEED_CACHE_MAX_AGE,
  deleteJsonFeed,
  fetchJsonFeed,
  fetchJsonFeeds,
  getPublicJsonFeedExportUrl,
  importJsonFeeds,
  isPublicJsonFeedEnabled,
  isPublicJsonFeedShortCache,
  normalizePublicJsonFeedCacheMaxAge,
  PUBLIC_JSON_FEED_CACHE_MAX_AGE_OPTIONS,
  type PublicJsonFeedCacheMaxAge,
  regenerateJsonFeedToken,
  updateJsonFeed
} from "../../api/publicJsonFeedsApi";
import { cn } from "@/lib/utils";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";
import { ArrowDownUp, Copy, Download, GripVertical, Plus, RefreshCw, Trash2, Upload } from "lucide-react";

type ComponentFieldMeta = {
  name: string;
  label?: string;
  type?: string;
  value?: string | boolean | number;
  options?: string[];
};

type ComponentMeta = {
  uid: string;
  name: string;
  slug: string;
  fields: ComponentFieldMeta[];
};

type FilterRow = { field: string; value: string };
type ExposeOption = { key: string; label: string };

type SourceWithFilters = PublicJsonFeedSource & { filterRows: FilterRow[] };
type SortSourceRow = { id: string; source: SourceWithFilters };

function parseSelectOptions(raw: unknown): string[] {
  if (raw == null) return [];
  if (Array.isArray(raw)) {
    return raw.map((o) => String(o).trim()).filter(Boolean);
  }
  if (typeof raw === "string") {
    return raw.split(",").map((s) => s.trim()).filter(Boolean);
  }
  return [];
}

function fieldByName(comp: ComponentMeta | undefined, fieldName: string): ComponentFieldMeta | undefined {
  if (!comp?.fields || !fieldName) return undefined;
  return comp.fields.find((f) => f?.name === fieldName);
}

/** Export field keys and labels; single definition for UI and ACL checks. */
function buildExposeOptions(comp: ComponentMeta | undefined, all: ComponentMeta[]): ExposeOption[] {
  const exposeOptions: ExposeOption[] = [];
  if (!comp) return exposeOptions;
  exposeOptions.push({ key: "uid", label: "UID" });
  for (const field of comp.fields || []) {
    if (!field?.name) continue;
    if (field.name === "uid") continue;
    exposeOptions.push({
      key: field.name,
      label: field.label || field.name
    });
    if (field.type === "relation" || field.type === "multiplerelation") {
      const relatedSlug = typeof field.value === "string" ? field.value : "";
      if (!relatedSlug) continue;
      const related = all.find((c) => c.slug === relatedSlug);
      if (!related) continue;
      for (const relatedField of related.fields || []) {
        if (!relatedField?.name) continue;
        exposeOptions.push({
          key: `${field.name}.${relatedField.name}`,
          label: `${field.label || field.name} -> ${relatedField.label || relatedField.name}`
        });
      }
    }
  }
  return exposeOptions;
}

const emptySource = (): SourceWithFilters => ({
  key: "",
  componentSlug: "",
  fields: [],
  filters: {},
  filterRows: [{ field: "", value: "" }]
});

const filtersFromRows = (
  rows: FilterRow[],
  comp: ComponentMeta | undefined
): Record<string, string | string[] | boolean> => {
  const out: Record<string, string | string[] | boolean> = {};
  for (const row of rows) {
    const f = row.field.trim();
    if (!f) continue;
    const meta = fieldByName(comp, f);
    const raw = row.value.trim();

    if (meta?.type === "checkbox") {
      if (!raw) continue;
      const lower = raw.toLowerCase();
      if (lower === "true" || lower === "1" || lower === "yes") out[f] = true;
      else if (lower === "false" || lower === "0" || lower === "no") out[f] = false;
      continue;
    }

    if (!raw) continue;
    if (raw.includes(",")) {
      const parts = raw.split(",").map((s) => s.trim()).filter(Boolean);
      if (parts.length) out[f] = parts.length === 1 ? parts[0] : parts;
    } else {
      out[f] = raw;
    }
  }
  return out;
};

const rowsFromFilters = (filters: Record<string, string | string[] | boolean>): FilterRow[] => {
  const entries = Object.entries(filters || {});
  if (entries.length === 0) return [{ field: "", value: "" }];
  return entries.map(([field, value]) => ({
    field,
    value:
      typeof value === "boolean"
        ? value
          ? "true"
          : "false"
        : Array.isArray(value)
          ? value.join(", ")
          : String(value ?? "")
  }));
};

function normalizeFeedSourceForImport(s: unknown): PublicJsonFeedSource | null {
  if (!s || typeof s !== "object") return null;
  const o = s as Record<string, unknown>;
  if (typeof o.key !== "string" || typeof o.componentSlug !== "string") return null;
  if (!Array.isArray(o.fields)) return null;
  const filtersRaw = o.filters;
  const filters =
    filtersRaw && typeof filtersRaw === "object" && !Array.isArray(filtersRaw)
      ? (filtersRaw as Record<string, string | string[] | boolean>)
      : {};
  return {
    key: o.key.trim(),
    componentSlug: o.componentSlug.trim(),
    fields: o.fields.map((f) => String(f)).filter(Boolean),
    filters
  };
}

function isFeedCandidate(x: unknown): x is { title: string; sources: unknown[] } {
  if (!x || typeof x !== "object") return false;
  const o = x as Record<string, unknown>;
  return typeof o.title === "string" && o.title.trim() !== "" && Array.isArray(o.sources);
}

function feedFromParsed(x: unknown): Partial<PublicJsonFeed> | null {
  if (!isFeedCandidate(x)) return null;
  const sources = x.sources.map(normalizeFeedSourceForImport).filter(Boolean) as PublicJsonFeedSource[];
  if (sources.length === 0) return null;
  const row: Partial<PublicJsonFeed> = {
    title: x.title.trim(),
    sources
  };
  const ext = x as Record<string, unknown>;
  if (ext.status === "draft" || ext.status === "published") row.status = ext.status;
  if (typeof ext.uid === "string" && ext.uid.trim()) row.uid = ext.uid.trim();
  if (typeof ext.publicToken === "string" && ext.publicToken.trim()) row.publicToken = ext.publicToken.trim();
  if (ext.enabled === false) row.enabled = false;
  else if (ext.enabled === true) row.enabled = true;
  if (ext.cacheMaxAgeSeconds != null) {
    row.cacheMaxAgeSeconds = normalizePublicJsonFeedCacheMaxAge(ext.cacheMaxAgeSeconds);
  }
  return row;
}

function parsePublicJsonFeedImportData(data: unknown): Partial<PublicJsonFeed>[] {
  const out: Partial<PublicJsonFeed>[] = [];
  const pushFrom = (raw: unknown) => {
    const p = feedFromParsed(raw);
    if (p) out.push(p);
  };
  const unwrap = (raw: unknown): void => {
    if (raw == null) return;
    if (Array.isArray(raw)) {
      for (const item of raw) pushFrom(item);
      return;
    }
    if (typeof raw !== "object") return;
    const o = raw as Record<string, unknown>;
    if (o.labwickPublicJsonFeedExport && typeof o.labwickPublicJsonFeedExport === "object") {
      unwrap(o.labwickPublicJsonFeedExport);
      return;
    }
    if (Array.isArray(o.feeds)) {
      for (const f of o.feeds) pushFrom(f);
      return;
    }
    if (o.feed !== undefined && o.feed !== null && isFeedCandidate(o.feed)) {
      pushFrom(o.feed);
      return;
    }
    pushFrom(o);
  };
  unwrap(data);
  return out;
}

function findComponentBySlug(slug: string, all: ComponentMeta[]): ComponentMeta | undefined {
  const t = slug.trim().toLowerCase();
  if (!t) return undefined;
  return all.find((c) => c.slug.toLowerCase() === t);
}

function validExposeKeySet(comp: ComponentMeta | undefined, all: ComponentMeta[]): Set<string> {
  if (!comp) return new Set();
  return new Set(buildExposeOptions(comp, all).map((o) => o.key));
}

type ImportSourceResolution = {
  key: string;
  originalSlug: string;
  originalFields: string[];
  filters: Record<string, string | string[] | boolean>;
  skipped: boolean;
  componentSlug: string;
  selectedFields: string[];
};

type ImportFeedBundle = {
  meta: Partial<PublicJsonFeed>;
  sources: ImportSourceResolution[];
};

function buildImportBundles(feeds: Partial<PublicJsonFeed>[], components: ComponentMeta[]): ImportFeedBundle[] {
  return feeds.map((meta) => ({
    meta,
    sources: (meta.sources || []).map((src) => {
      const comp = findComponentBySlug(src.componentSlug, components);
      const valid = validExposeKeySet(comp, components);
      const originalFields = [...src.fields];
      const selectedFields = comp ? originalFields.filter((f) => valid.has(f)) : [];
      return {
        key: src.key,
        originalSlug: src.componentSlug,
        originalFields,
        filters: src.filters,
        skipped: false,
        componentSlug: comp ? comp.slug : "",
        selectedFields
      };
    })
  }));
}

function computeFieldsForSlug(
  originalFields: string[],
  componentSlug: string,
  components: ComponentMeta[]
): string[] {
  const comp = findComponentBySlug(componentSlug, components);
  if (!comp) return [];
  const valid = validExposeKeySet(comp, components);
  return originalFields.filter((f) => valid.has(f));
}

function bundlesToImportPayload(bundles: ImportFeedBundle[]): Partial<PublicJsonFeed>[] {
  return bundles.map((b) => ({
    ...b.meta,
    sources: b.sources
      .filter((s) => !s.skipped)
      .map((s) => ({
        key: s.key.trim(),
        componentSlug: s.componentSlug.trim(),
        fields: s.selectedFields,
        filters: s.filters
      }))
  }));
}

function mapApiToComponentMeta(list: unknown): ComponentMeta[] {
  const arr = Array.isArray(list) ? list : [];
  return arr.map((c: any) => ({
    uid: c.uid,
    name: c.name,
    slug: c.slug,
    fields: Array.isArray(c.fields)
      ? c.fields.map((fld: any) => ({
        name: fld.name,
        label: fld.label,
        type: fld.type,
        value: fld.value,
        options: fld.options
      }))
      : []
  }));
}

const PublicJsonFeedsManager = () => {
  const [components, setComponents] = useState<ComponentMeta[]>([]);
  const [feeds, setFeeds] = useState<PublicJsonFeed[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingUid, setEditingUid] = useState<string | null>(null);
  const [title, setTitle] = useState("");
  const [status, setStatus] = useState<"draft" | "published">("draft");
  const [cacheMaxAgeSeconds, setCacheMaxAgeSeconds] = useState<PublicJsonFeedCacheMaxAge>(
    DEFAULT_PUBLIC_JSON_FEED_CACHE_MAX_AGE
  );
  const [sources, setSources] = useState<SourceWithFilters[]>([emptySource()]);
  const [sortDialogOpen, setSortDialogOpen] = useState(false);
  const [sortRows, setSortRows] = useState<SortSourceRow[]>([]);
  const [sortDragIndex, setSortDragIndex] = useState<number | null>(null);
  const [sortDropIndex, setSortDropIndex] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const [copiedToken, setCopiedToken] = useState<string | null>(null);
  const [fieldAclFlags, setFieldAclFlags] = useState<Record<number, Record<string, boolean>>>({});
  const [fieldAclLoading, setFieldAclLoading] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importBundles, setImportBundles] = useState<ImportFeedBundle[]>([]);
  const [importComponents, setImportComponents] = useState<ComponentMeta[]>([]);
  const [importScanning, setImportScanning] = useState(false);
  const [importError, setImportError] = useState<string | null>(null);
  const [importing, setImporting] = useState(false);
  const [importFieldAclFlags, setImportFieldAclFlags] = useState<Record<string, Record<string, boolean>>>({});
  const [importFieldAclLoading, setImportFieldAclLoading] = useState(false);
  const [enablingUid, setEnablingUid] = useState<string | null>(null);
  const importFileRef = useRef<HTMLInputElement>(null);
  const importAclFetchSeq = useRef(0);

  const importAclKey = (feedIndex: number, sourceIndex: number) => `${feedIndex}-${sourceIndex}`;

  const availableComponents = useMemo(() => components, [components]);

  const importCanSubmit = useMemo(() => {
    if (importBundles.length === 0 || importScanning || importFieldAclLoading) return false;
    if (importComponents.length === 0) return false;
    for (let fi = 0; fi < importBundles.length; fi++) {
      const b = importBundles[fi];
      const active = b.sources.filter((s) => !s.skipped);
      if (active.length === 0) return false;
      for (let si = 0; si < b.sources.length; si++) {
        const s = b.sources[si];
        if (s.skipped) continue;
        if (!s.key.trim()) return false;
        const slug = s.componentSlug.trim();
        if (!slug) return false;
        const comp = findComponentBySlug(slug, importComponents);
        if (!comp) return false;
        const valid = validExposeKeySet(comp, importComponents);
        const flags = importFieldAclFlags[importAclKey(fi, si)];
        if (!flags || Object.keys(flags).length === 0) return false;
        for (const f of s.selectedFields) {
          if (!valid.has(f)) return false;
          if (flags[f] !== true) return false;
        }
      }
    }
    return true;
  }, [importBundles, importComponents, importScanning, importFieldAclLoading, importFieldAclFlags]);

  const clearImportAclForSource = useCallback((feedIndex: number, sourceIndex: number) => {
    const k = `${feedIndex}-${sourceIndex}`;
    setImportFieldAclFlags((prev) => {
      if (!(k in prev)) return prev;
      const next = { ...prev };
      delete next[k];
      return next;
    });
  }, []);

  const patchImportSource = useCallback(
    (feedIndex: number, sourceIndex: number, patch: Partial<ImportSourceResolution>) => {
      setImportBundles((prev) =>
        prev.map((row, fi) => {
          if (fi !== feedIndex) return row;
          return {
            ...row,
            sources: row.sources.map((s, si) => (si === sourceIndex ? { ...s, ...patch } : s))
          };
        })
      );
    },
    []
  );

  const setImportSourceCollection = useCallback(
    (feedIndex: number, sourceIndex: number, value: string) => {
      clearImportAclForSource(feedIndex, sourceIndex);
      if (value === "__skip__") {
        patchImportSource(feedIndex, sourceIndex, { skipped: true, componentSlug: "", selectedFields: [] });
        return;
      }
      if (value === "__unpick__") return;
      const comp = findComponentBySlug(value, importComponents);
      if (!comp) return;
      setImportBundles((prev) =>
        prev.map((row, fi) => {
          if (fi !== feedIndex) return row;
          return {
            ...row,
            sources: row.sources.map((s, si) => {
              if (si !== sourceIndex) return s;
              const selectedFields = computeFieldsForSlug(s.originalFields, comp.slug, importComponents);
              return {
                ...s,
                skipped: false,
                componentSlug: comp.slug,
                selectedFields
              };
            })
          };
        })
      );
    },
    [importComponents, patchImportSource, clearImportAclForSource]
  );

  const toggleImportSourceField = useCallback(
    (feedIndex: number, sourceIndex: number, fieldKey: string, checked: boolean) => {
      const k = `${feedIndex}-${sourceIndex}`;
      const flags = importFieldAclFlags[k];
      if (checked) {
        if (importFieldAclLoading) return;
        if (!flags || flags[fieldKey] !== true) return;
      }
      setImportBundles((prev) =>
        prev.map((row, fi) => {
          if (fi !== feedIndex) return row;
          return {
            ...row,
            sources: row.sources.map((s, si) => {
              if (si !== sourceIndex) return s;
              const next = new Set(s.selectedFields);
              if (checked) next.add(fieldKey);
              else next.delete(fieldKey);
              return { ...s, selectedFields: Array.from(next) };
            })
          };
        })
      );
    },
    [importFieldAclFlags, importFieldAclLoading]
  );

  const selectAllAvailableImportFields = useCallback((feedIndex: number, sourceIndex: number) => {
    if (importFieldAclLoading) return;
    setImportBundles((prev) => {
      const row = prev[feedIndex];
      if (!row) return prev;
      const s = row.sources[sourceIndex];
      if (!s || s.skipped) return prev;
      const comp = findComponentBySlug(s.componentSlug.trim(), importComponents);
      if (!comp) return prev;
      const exposeOptions = buildExposeOptions(comp, importComponents);
      const aclFlags = importFieldAclFlags[`${feedIndex}-${sourceIndex}`];
      const keys = exposeOptions
        .filter((opt) => aclFlags && aclFlags[opt.key] === true)
        .map((opt) => opt.key);
      if (keys.length === 0) return prev;
      return prev.map((bundle, fi) => {
        if (fi !== feedIndex) return bundle;
        return {
          ...bundle,
          sources: bundle.sources.map((src, si) =>
            si === sourceIndex ? { ...src, selectedFields: keys } : src
          )
        };
      });
    });
  }, [importFieldAclLoading, importComponents, importFieldAclFlags]);

  const loadImportFieldAclFlags = useCallback(async () => {
    if (!importDialogOpen || importBundles.length === 0 || importComponents.length === 0) return;
    const seq = ++importAclFetchSeq.current;
    setImportFieldAclLoading(true);
    const flagsOut: Record<string, Record<string, boolean>> = {};
    for (let fi = 0; fi < importBundles.length; fi++) {
      const row = importBundles[fi];
      for (let si = 0; si < row.sources.length; si++) {
        const s = row.sources[si];
        const k = `${fi}-${si}`;
        if (s.skipped || !s.componentSlug.trim()) {
          flagsOut[k] = {};
          continue;
        }
        const comp = findComponentBySlug(s.componentSlug.trim(), importComponents);
        if (!comp?.uid) {
          flagsOut[k] = {};
          continue;
        }
        const keys = buildExposeOptions(comp, importComponents).map((o) => o.key);
        if (keys.length === 0) {
          flagsOut[k] = {};
          continue;
        }
        try {
          flagsOut[k] = await aclAPI.getPublicJsonFeedFieldFlags(comp.uid, keys);
        } catch {
          flagsOut[k] = Object.fromEntries(keys.map((key) => [key, false]));
        }
      }
    }
    if (seq !== importAclFetchSeq.current) return;
    setImportFieldAclFlags(flagsOut);
    setImportFieldAclLoading(false);
  }, [importDialogOpen, importBundles, importComponents]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [compRes, feedList] = await Promise.all([
        fetchComponentsFromAPI(),
        fetchJsonFeeds()
      ]);
      const list = Array.isArray(compRes) ? compRes : [];
      setComponents(mapApiToComponentMeta(list));
      setFeeds(feedList);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!dialogOpen) {
      setSortDialogOpen(false);
    }
  }, [dialogOpen]);

  useEffect(() => {
    if (!dialogOpen) {
      setFieldAclFlags({});
      setFieldAclLoading(false);
      return;
    }
    let cancelled = false;
    const timer = window.setTimeout(async () => {
      setFieldAclLoading(true);
      const flagsOut: Record<number, Record<string, boolean>> = {};
      for (let si = 0; si < sources.length; si++) {
        const comp = availableComponents.find((c) => c.slug === sources[si].componentSlug.trim());
        if (!comp?.uid) {
          flagsOut[si] = {};
          continue;
        }
        const keys = buildExposeOptions(comp, availableComponents).map((o) => o.key);
        try {
          flagsOut[si] = await aclAPI.getPublicJsonFeedFieldFlags(comp.uid, keys);
        } catch {
          flagsOut[si] = Object.fromEntries(keys.map((k) => [k, false]));
        }
      }
      if (!cancelled) {
        setFieldAclFlags(flagsOut);
        setFieldAclLoading(false);
      }
    }, 320);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [dialogOpen, sources, availableComponents]);

  useEffect(() => {
    if (!dialogOpen) return;
    setSources((prev) => {
      let changed = false;
      const next = prev.map((s, si) => {
        const flags = fieldAclFlags[si];
        if (!flags || Object.keys(flags).length === 0) return s;
        const pruned = s.fields.filter((f) => flags[f] === true);
        if (pruned.length === s.fields.length) return s;
        changed = true;
        return { ...s, fields: pruned };
      });
      return changed ? next : prev;
    });
  }, [dialogOpen, fieldAclFlags]);

  useEffect(() => {
    if (!importDialogOpen) {
      importAclFetchSeq.current += 1;
      setImportFieldAclFlags({});
      setImportFieldAclLoading(false);
      return;
    }
    if (importBundles.length === 0 || importComponents.length === 0) {
      importAclFetchSeq.current += 1;
      setImportFieldAclLoading(false);
      return;
    }
    const timer = window.setTimeout(() => {
      void loadImportFieldAclFlags();
    }, 320);
    return () => {
      clearTimeout(timer);
      importAclFetchSeq.current += 1;
    };
  }, [importDialogOpen, importBundles, importComponents, loadImportFieldAclFlags]);

  useEffect(() => {
    if (!importDialogOpen) return;
    setImportBundles((prev) => {
      let changed = false;
      const next = prev.map((row, fi) => ({
        ...row,
        sources: row.sources.map((s, si) => {
          const k = `${fi}-${si}`;
          const flags = importFieldAclFlags[k];
          if (!flags || Object.keys(flags).length === 0) return s;
          const pruned = s.selectedFields.filter((f) => flags[f] === true);
          if (pruned.length === s.selectedFields.length) return s;
          changed = true;
          return { ...s, selectedFields: pruned };
        }),
      }));
      return changed ? next : prev;
    });
  }, [importDialogOpen, importFieldAclFlags]);

  const openNew = () => {
    setEditingUid(null);
    setTitle("");
    setStatus("draft");
    setCacheMaxAgeSeconds(DEFAULT_PUBLIC_JSON_FEED_CACHE_MAX_AGE);
    setSources([emptySource()]);
    setDialogOpen(true);
  };

  const openEdit = (feed: PublicJsonFeed) => {
    setEditingUid(feed.uid);
    setTitle(feed.title);
    setStatus(feed.status);
    setCacheMaxAgeSeconds(normalizePublicJsonFeedCacheMaxAge(feed.cacheMaxAgeSeconds));
    setSources(
      (feed.sources || []).map((s) => ({
        ...s,
        filterRows: rowsFromFilters(s.filters || {})
      }))
    );
    if (!feed.sources?.length) {
      setSources([emptySource()]);
    }
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setEditingUid(null);
  };

  const addSource = () => {
    setSources((prev) => [...prev, emptySource()]);
  };

  const removeSource = (index: number) => {
    setSources((prev) => prev.filter((_, i) => i !== index));
  };

  const updateSource = (index: number, patch: Partial<PublicJsonFeedSource & { filterRows?: FilterRow[] }>) => {
    setSources((prev) =>
      prev.map((s, i) => (i === index ? { ...s, ...patch } : s))
    );
  };

  const openSortSourcesDialog = () => {
    setSortRows(
      sources.map((s, i) => ({
        id:
          typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
            ? crypto.randomUUID()
            : `sort-row-${i}-${Date.now()}`,
        source: s
      }))
    );
    setSortDragIndex(null);
    setSortDropIndex(null);
    setSortDialogOpen(true);
  };

  const applySortSourcesOrder = () => {
    setSources(sortRows.map((r) => r.source));
    setFieldAclFlags({});
    setSortDialogOpen(false);
    setSortDragIndex(null);
    setSortDropIndex(null);
  };

  const clearSortDragState = () => {
    setSortDragIndex(null);
    setSortDropIndex(null);
  };

  const handleSortDragStart = (e: React.DragEvent, index: number) => {
    setSortDragIndex(index);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", String(index));
  };

  const handleSortDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = "move";
    setSortDropIndex(index);
  };

  const handleSortDragLeaveRow = (e: React.DragEvent) => {
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      setSortDropIndex(null);
    }
  };

  const handleSortDrop = (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();
    e.stopPropagation();
    const fromData = e.dataTransfer.getData("text/plain");
    const parsed = fromData ? parseInt(fromData, 10) : NaN;
    const sourceIndex = sortDragIndex ?? (Number.isFinite(parsed) ? parsed : null);
    if (sourceIndex === null || sourceIndex === dropIndex) {
      clearSortDragState();
      return;
    }
    setSortRows((prev) => {
      const next = [...prev];
      const [moved] = next.splice(sourceIndex, 1);
      next.splice(dropIndex, 0, moved);
      return next;
    });
    clearSortDragState();
  };

  const handleSortDragEnd = () => {
    clearSortDragState();
  };

  const toggleField = (sourceIndex: number, fieldName: string, checked: boolean) => {
    setSources((prev) =>
      prev.map((s, i) => {
        if (i !== sourceIndex) return s;
        const set = new Set(s.fields);
        if (checked) set.add(fieldName);
        else set.delete(fieldName);
        return { ...s, fields: Array.from(set) };
      })
    );
  };

  const selectAllAvailableEditFields = useCallback((sourceIndex: number) => {
    if (fieldAclLoading) return;
    setSources((prev) => {
      const src = prev[sourceIndex];
      if (!src) return prev;
      const comp = availableComponents.find((c) => c.slug === src.componentSlug);
      if (!comp) return prev;
      const exposeOptions = buildExposeOptions(comp, availableComponents);
      const flags = fieldAclFlags[sourceIndex];
      const keys = exposeOptions
        .filter((opt) => flags && flags[opt.key] === true)
        .map((opt) => opt.key);
      if (keys.length === 0) return prev;
      return prev.map((s, i) => (i === sourceIndex ? { ...s, fields: keys } : s));
    });
  }, [availableComponents, fieldAclFlags, fieldAclLoading]);

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const payloadSources: PublicJsonFeedSource[] = sources.map((s) => {
        const comp = availableComponents.find((c) => c.slug === s.componentSlug.trim());
        return {
          key: s.key.trim(),
          componentSlug: s.componentSlug.trim(),
          fields: [...s.fields],
          filters: filtersFromRows(s.filterRows, comp)
        };
      });

      if (editingUid) {
        await updateJsonFeed(editingUid, {
          title,
          status,
          cacheMaxAgeSeconds,
          sources: payloadSources
        });
      } else {
        await createJsonFeed({ title, status, cacheMaxAgeSeconds, sources: payloadSources });
      }
      closeDialog();
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const removeFeed = async (uid: string) => {
    if (!confirm("Delete this feed? The public URL will stop working.")) return;
    try {
      await deleteJsonFeed(uid);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Delete failed");
    }
  };

  const regen = async (uid: string) => {
    if (!confirm("Regenerate token? Old URLs will stop working.")) return;
    try {
      await regenerateJsonFeedToken(uid);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Regenerate failed");
    }
  };

  const setFeedEnabled = async (feed: PublicJsonFeed, enabled: boolean) => {
    setEnablingUid(feed.uid);
    setError(null);
    const previous = feeds;
    setFeeds((list) => list.map((f) => (f.uid === feed.uid ? { ...f, enabled } : f)));
    try {
      const updated = await updateJsonFeed(feed.uid, { enabled });
      setFeeds((list) => list.map((f) => (f.uid === feed.uid ? updated : f)));
    } catch (e) {
      setFeeds(previous);
      setError(e instanceof Error ? e.message : "Failed to update feed");
    } finally {
      setEnablingUid(null);
    }
  };

  const copyUrl = async (token: string) => {
    const url = getPublicJsonFeedExportUrl(token);
    try {
      await navigator.clipboard.writeText(url);
      setCopiedToken(token);
      setTimeout(() => setCopiedToken(null), 2000);
    } catch {
      setError("Could not copy to clipboard");
    }
  };

  const exportFeedAsJson = async (feed: PublicJsonFeed) => {
    setError(null);
    try {
      const full = await fetchJsonFeed(feed.uid);
      const payload = {
        version: 1,
        exportedAt: new Date().toISOString(),
        feed: full
      };
      const blob = new Blob([JSON.stringify(payload, null, 2)], { type: "application/json" });
      const name = (full.title || "feed").replace(/[^a-z0-9_-]/gi, "_");
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${name}-public-json-feed.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Export failed");
    }
  };

  const exportAllFeedsJson = async () => {
    if (feeds.length === 0) return;
    setError(null);
    try {
      const fullList = await Promise.all(feeds.map((f) => fetchJsonFeed(f.uid)));
      const payload = {
        version: 1,
        exportedAt: new Date().toISOString(),
        feeds: fullList
      };
      const blob = new Blob([JSON.stringify(payload, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "public-json-feeds-all.json";
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Export failed");
    }
  };

  const startImportFromJson = () => {
    setImportBundles([]);
    setImportComponents([]);
    setImportError(null);
    setImportFieldAclFlags({});
    setImportFieldAclLoading(false);
    importFileRef.current?.click();
  };

  const handleImportFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      void (async () => {
        const text = typeof reader.result === "string" ? reader.result : "";
        let data: unknown;
        try {
          data = JSON.parse(text);
        } catch {
          setImportError("Invalid JSON.");
          setImportBundles([]);
          setImportComponents([]);
          setImportDialogOpen(true);
          return;
        }
        const parsed = parsePublicJsonFeedImportData(data);
        if (parsed.length === 0) {
          setImportError(
            "No valid feeds found. Expected an object with title and sources[], or feeds[], or a labwickPublicJsonFeedExport wrapper."
          );
          setImportBundles([]);
          setImportComponents([]);
          setImportDialogOpen(true);
          return;
        }
        setImportScanning(true);
        setImportError(null);
        try {
          const compRes = await fetchComponentsFromAPI();
          const list = mapApiToComponentMeta(compRes);
          setImportComponents(list);
          setImportBundles(buildImportBundles(parsed, list));
          if (list.length === 0) {
            setImportError("No collections are available in this environment. Cannot map imported sources.");
          }
          setImportDialogOpen(true);
        } catch (err) {
          setImportError(err instanceof Error ? err.message : "Failed to load collections");
          setImportBundles([]);
          setImportComponents([]);
          setImportDialogOpen(true);
        } finally {
          setImportScanning(false);
        }
      })();
    };
    reader.readAsText(file);
  };

  const confirmImportFeeds = async () => {
    if (!importCanSubmit) return;
    const payload = bundlesToImportPayload(importBundles);
    setImporting(true);
    setImportError(null);
    try {
      const result = await importJsonFeeds({ feeds: payload, mode: "create" });
      setImportDialogOpen(false);
      setImportBundles([]);
      setImportComponents([]);
      if (result.errors?.length) {
        const detail = result.errors.map((err) => `#${err.index}: ${err.message}`).join("; ");
        setError(`Import finished with errors: ${detail}`);
      } else {
        setError(null);
      }
      await load();
    } catch (e) {
      setImportError(e instanceof Error ? e.message : "Import failed");
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-6">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Public JSON feeds</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Aggregated read-only JSON per secret URL. Each collection must be marked{" "}
            <span className="font-medium">published in ACL</span> and have ACL: WHO{" "}
            <span className="font-medium">ROLE public</span>, ACTION{" "}
            <span className="font-medium">public_publish_collection</span> (or field-level <span className="font-medium">public_expose_fields</span>) for the source collection.
          </p>
        </div>
        <div className="flex flex-wrap gap-2 shrink-0">
          <Button type="button" variant="outline" onClick={startImportFromJson}>
            <Upload className="w-4 h-4 mr-2" />
            Import
          </Button>
          <Button type="button" variant="outline" onClick={exportAllFeedsJson} disabled={feeds.length === 0}>
            <Download className="w-4 h-4 mr-2" />
            Export all
          </Button>
          <Button onClick={openNew}>
            <Plus className="w-4 h-4 mr-2" />
            New feed
          </Button>
        </div>
      </div>
      <input
        ref={importFileRef}
        type="file"
        accept=".json,application/json"
        className="hidden"
        onChange={handleImportFileChange}
      />

      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {loading ? (
        <p className="text-muted-foreground">Loading…</p>
      ) : feeds.length === 0 ? (
        <Card>
          <CardContent className="pt-6 text-muted-foreground">No feeds yet. Create one to expose JSON.</CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {feeds.map((feed) => (
            <Card
              key={feed.uid}
              className={cn(!isPublicJsonFeedEnabled(feed) && "opacity-70")}
            >
              <CardHeader className="flex flex-row items-start justify-between gap-4">
                <div className="space-y-2 min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-3">
                    <div className="flex items-center gap-2">
                      <Switch
                        id={`feed-enabled-${feed.uid}`}
                        checked={isPublicJsonFeedEnabled(feed)}
                        disabled={enablingUid === feed.uid}
                        onCheckedChange={(checked) => void setFeedEnabled(feed, checked)}
                      />
                      <Label htmlFor={`feed-enabled-${feed.uid}`} className="text-sm font-medium cursor-pointer">
                        Enabled
                      </Label>
                    </div>
                    {isPublicJsonFeedShortCache(feed) ? (
                      <span className="inline-flex rounded-full px-2 py-0.5 text-xs font-medium bg-amber-50 text-amber-800 ring-1 ring-amber-200 dark:bg-amber-950/50 dark:text-amber-100 dark:ring-amber-700">
                        Short cache — switch back to Default when you are done testing
                      </span>
                    ) : null}
                  </div>
                  <CardTitle className="text-lg">{feed.title}</CardTitle>
                  <p className="text-sm text-muted-foreground mt-1">
                    Status: {feed.status}
                    {!isPublicJsonFeedEnabled(feed) ? " · disabled" : ""}
                    {" · "}
                    Browser cache:{" "}
                    {PUBLIC_JSON_FEED_CACHE_MAX_AGE_OPTIONS.find(
                      (opt) => opt.value === normalizePublicJsonFeedCacheMaxAge(feed.cacheMaxAgeSeconds)
                    )?.label ?? "Default (300s)"}
                    {" · "}
                    {(feed.sources || []).length} source(s)
                  </p>
                  <p className="text-xs font-mono mt-2 break-all">
                    {getPublicJsonFeedExportUrl(feed.publicToken)}
                  </p>
                </div>
                <div className="flex flex-wrap gap-2 shrink-0">
                  <Button variant="outline" size="sm" onClick={() => exportFeedAsJson(feed)}>
                    <Download className="w-4 h-4 mr-1" />
                    Export
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => copyUrl(feed.publicToken)}>
                    <Copy className="w-4 h-4 mr-1" />
                    {copiedToken === feed.publicToken ? "Copied" : "Copy URL"}
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => regen(feed.uid)}>
                    <RefreshCw className="w-4 h-4 mr-1" />
                    New token
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => openEdit(feed)}>
                    Edit
                  </Button>
                  <Button variant="destructive" size="sm" onClick={() => removeFeed(feed.uid)}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </CardHeader>
            </Card>
          ))}
        </div>
      )}

      <Dialog
        open={importDialogOpen}
        onOpenChange={(o) => {
          if (!o) {
            setImportDialogOpen(false);
            setImportBundles([]);
            setImportComponents([]);
            setImportError(null);
            setImportScanning(false);
            setImportFieldAclFlags({});
            setImportFieldAclLoading(false);
          }
        }}
      >
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Import JSON feeds</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 text-sm">
            <p className="text-muted-foreground">
              Collections and fields are checked against this environment. Map each source to an existing collection or
              skip it. Exposed fields must exist on the collection (including relation paths) and match{" "}
              <span className="font-medium">public ACL</span> (ROLE public / public_publish_collection /{" "}
              public_expose_fields), same as the New feed form. Import stays disabled until ACL checks finish and every
              selected field is allowed.{" "}
              <span className="font-medium">Import always creates new feeds</span> (new uids and tokens; ids in the
              file are ignored).
            </p>

            {importScanning ? (
              <p className="text-muted-foreground">Loading collections…</p>
            ) : importBundles.length > 0 ? (
              <div className="space-y-4">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <p className="text-xs text-muted-foreground">
                    {importFieldAclLoading
                      ? "Checking public ACL (ROLE public) for exposed fields…"
                      : "ACL checks use current server rules. After changing permissions in ACL Manager, recheck before importing."}
                  </p>
                  {importComponents.length > 0 && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled={importFieldAclLoading}
                      onClick={() => void loadImportFieldAclFlags()}
                    >
                      <RefreshCw className="w-4 h-4 mr-1" />
                      Recheck ACLs
                    </Button>
                  )}
                </div>
                {importBundles.map((bundle, feedIndex) => {
                  const title = bundle.meta.title?.trim() || "(untitled)";
                  const activeCount = bundle.sources.filter((s) => !s.skipped).length;
                  return (
                    <Card key={feedIndex}>
                      <CardHeader className="py-3 px-4">
                        <CardTitle className="text-base">
                          Feed {feedIndex + 1}: {title}
                        </CardTitle>
                        <p className="text-xs text-muted-foreground font-normal">
                          {activeCount === 0
                            ? "All sources skipped — add at least one source or cancel."
                            : `${activeCount} source(s) included`}
                        </p>
                      </CardHeader>
                      <CardContent className="space-y-4 px-4 pb-4">
                        {bundle.sources.map((src, sourceIndex) => {
                          const comp = src.componentSlug
                            ? findComponentBySlug(src.componentSlug, importComponents)
                            : undefined;
                          const exposeOptions = buildExposeOptions(comp, importComponents);
                          const selectValue = src.skipped
                            ? "__skip__"
                            : src.componentSlug.trim()
                              ? src.componentSlug
                              : "__unpick__";
                          const valid = comp ? validExposeKeySet(comp, importComponents) : new Set<string>();
                          const aclFlags = importFieldAclFlags[`${feedIndex}-${sourceIndex}`];
                          const dropped =
                            comp && src.originalFields.length
                              ? src.originalFields.filter((f) => !valid.has(f))
                              : [];
                          const aclBlocked =
                            comp && src.originalFields.length && aclFlags && !importFieldAclLoading
                              ? src.originalFields.filter((f) => valid.has(f) && aclFlags[f] !== true)
                              : [];
                          const canSelectAllImportFields = exposeOptions.some(
                            (opt) => aclFlags && aclFlags[opt.key] === true
                          );
                          return (
                            <div
                              key={`${feedIndex}-${sourceIndex}`}
                              className="rounded-md border p-3 space-y-2 bg-muted/30"
                            >
                              <div className="flex flex-wrap items-center justify-between gap-2">
                                <span className="font-medium text-sm">JSON key: {src.key || "(empty)"}</span>
                              </div>
                              <p className="text-xs text-muted-foreground">
                                From file: collection{" "}
                                <span className="font-mono">{src.originalSlug || "—"}</span>
                                {src.originalFields.length > 0
                                  ? ` · ${src.originalFields.length} field path(s)`
                                  : ""}
                              </p>
                              <div>
                                <Label className="text-xs">Collection</Label>
                                <Select
                                  value={selectValue}
                                  onValueChange={(v) => setImportSourceCollection(feedIndex, sourceIndex, v)}
                                >
                                  <SelectTrigger className="mt-1">
                                    <SelectValue placeholder="Select collection" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="__skip__">Skip this source</SelectItem>
                                    <SelectItem value="__unpick__" disabled>
                                      Select collection…
                                    </SelectItem>
                                    {importComponents.map((c) => (
                                      <SelectItem key={c.uid} value={c.slug}>
                                        {c.name} ({c.slug})
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>
                              {!src.skipped && comp && (
                                <div>
                                  <Label className="text-xs">Fields to expose</Label>
                                  <div className="mt-1">
                                    <Button
                                      type="button"
                                      variant="outline"
                                      size="sm"
                                      className="text-xs h-8"
                                      disabled={importFieldAclLoading || !canSelectAllImportFields}
                                      onClick={() =>
                                        selectAllAvailableImportFields(feedIndex, sourceIndex)
                                      }
                                    >
                                      Select all available fields
                                    </Button>
                                  </div>
                                  {dropped.length > 0 && (
                                    <p className="text-xs text-amber-700 dark:text-amber-500 mt-1">
                                      Not available on this collection (omitted unless you map another collection above):{" "}
                                      {dropped.map((d) => (
                                        <span key={d} className="font-mono mr-1">
                                          {d}
                                        </span>
                                      ))}
                                    </p>
                                  )}
                                  {aclBlocked.length > 0 && (
                                    <p className="text-xs text-amber-700 dark:text-amber-500 mt-1">
                                      Not exposed for unauthenticated public export (ACL):{" "}
                                      {aclBlocked.map((d) => (
                                        <span key={d} className="font-mono mr-1">
                                          {d}
                                        </span>
                                      ))}
                                    </p>
                                  )}
                                  <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-44 overflow-y-auto border rounded p-2">
                                    {exposeOptions.map((opt) => {
                                      const checked = src.selectedFields.includes(opt.key);
                                      const allowed =
                                        !importFieldAclLoading && aclFlags && aclFlags[opt.key] === true;
                                      const blocked =
                                        !importFieldAclLoading && aclFlags && aclFlags[opt.key] === false;
                                      const disableCheckbox = importFieldAclLoading || !allowed;
                                      return (
                                        <label
                                          key={opt.key}
                                          className={cn(
                                            "flex items-center gap-2 text-xs rounded px-1 py-0.5",
                                            blocked && "opacity-50"
                                          )}
                                          title={
                                            blocked
                                              ? "Not exposed for unauthenticated public export. Grant public_publish_collection or public_expose_fields on this field (and related collection for relation paths)."
                                              : undefined
                                          }
                                        >
                                          <Checkbox
                                            checked={checked}
                                            disabled={disableCheckbox}
                                            onCheckedChange={(c) => {
                                              if (disableCheckbox) return;
                                              toggleImportSourceField(feedIndex, sourceIndex, opt.key, c === true);
                                            }}
                                          />
                                          <span>{opt.label}</span>
                                        </label>
                                      );
                                    })}
                                  </div>
                                </div>
                              )}
                              {!src.skipped && !comp && (
                                <p className="text-xs text-destructive">
                                  Choose a collection above, or skip this source.
                                </p>
                              )}
                            </div>
                          );
                        })}
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            ) : (
              !importError && <p className="text-destructive">No feeds to import.</p>
            )}
            {importError && (
              <Alert variant="destructive">
                <AlertDescription>{importError}</AlertDescription>
              </Alert>
            )}
          </div>
          <div className="flex justify-end gap-2 pt-2 border-t">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setImportDialogOpen(false);
                setImportBundles([]);
                setImportComponents([]);
                setImportError(null);
                setImportScanning(false);
                setImportFieldAclFlags({});
                setImportFieldAclLoading(false);
              }}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={confirmImportFeeds}
              disabled={importing || !importCanSubmit}
            >
              {importing ? "Importing…" : "Import"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={dialogOpen} onOpenChange={(o) => !o && closeDialog()}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingUid ? "Edit feed" : "New feed"}</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label htmlFor="feed-title">Title</Label>
              <Input
                id="feed-title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="My aggregated API"
              />
            </div>
            <div>
              <Label>Status</Label>
              <Select value={status} onValueChange={(v) => setStatus(v as "draft" | "published")}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="draft">draft</SelectItem>
                  <SelectItem value="published">published</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Browser cache</Label>
              <p className="text-xs text-muted-foreground mt-1 mb-2">
                How long browsers may reuse the public JSON export without refetching.
              </p>
              <Select
                value={String(cacheMaxAgeSeconds)}
                onValueChange={(v) => setCacheMaxAgeSeconds(normalizePublicJsonFeedCacheMaxAge(Number(v)))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PUBLIC_JSON_FEED_CACHE_MAX_AGE_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={String(opt.value)}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-6 border-t pt-4">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <Label className="text-base">Sources</Label>
                <div className="flex flex-wrap gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={openSortSourcesDialog}
                    disabled={sources.length < 2}
                    title={sources.length < 2 ? "Add at least two sources to reorder" : undefined}
                  >
                    <ArrowDownUp className="w-4 h-4 mr-1" />
                    Reorder
                  </Button>
                  <Button type="button" variant="outline" size="sm" onClick={addSource}>
                    Add source
                  </Button>
                </div>
              </div>

              {sources.map((src, si) => {
                const comp = availableComponents.find((c) => c.slug === src.componentSlug);
                const exposeOptions = buildExposeOptions(comp, availableComponents);
                const exposeAclFlags = fieldAclFlags[si];
                const canSelectAllExposeFields = exposeOptions.some(
                  (opt) => exposeAclFlags && exposeAclFlags[opt.key] === true
                );
                return (
                  <Card key={si}>
                    <CardHeader className="py-3 px-4 flex flex-row items-center justify-between">
                      <CardTitle className="text-sm">Source {si + 1}</CardTitle>
                      {sources.length > 1 && (
                        <Button type="button" variant="ghost" size="sm" onClick={() => removeSource(si)}>
                          Remove
                        </Button>
                      )}
                    </CardHeader>
                    <CardContent className="space-y-3 px-4 pb-4">
                      <div>
                        <Label>JSON key (property name in response)</Label>
                        <Input
                          value={src.key}
                          onChange={(e) => updateSource(si, { key: e.target.value })}
                          placeholder="e.g. sharedItems"
                        />
                      </div>
                      <div>
                        <Label>Collection</Label>
                        <Select
                          value={src.componentSlug || "__none__"}
                          onValueChange={(v) =>
                            updateSource(si, {
                              componentSlug: v === "__none__" ? "" : v,
                              fields: [],
                              filterRows: [{ field: "", value: "" }]
                            })
                          }
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select collection" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="__none__">—</SelectItem>
                            {availableComponents.map((c) => (
                              <SelectItem key={c.uid} value={c.slug}>
                                {c.name} ({c.slug})
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      {comp && (
                        <div>
                          <Label>Fields to expose</Label>
                          <div className="mt-1">
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              disabled={fieldAclLoading || !canSelectAllExposeFields}
                              onClick={() => selectAllAvailableEditFields(si)}
                            >
                              Select all available fields
                            </Button>
                          </div>
                          {fieldAclLoading && (
                            <p className="text-xs text-muted-foreground mt-1">Checking public ACL (ROLE public)…</p>
                          )}
                          <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-40 overflow-y-auto border rounded p-2">
                            {exposeOptions.map((opt) => {
                              const flags = fieldAclFlags[si];
                              const allowed = !fieldAclLoading && flags && flags[opt.key] === true;
                              const blocked = !fieldAclLoading && flags && flags[opt.key] === false;
                              const disableCheckbox = fieldAclLoading || !allowed;
                              return (
                                <label
                                  key={opt.key}
                                  className={cn(
                                    "flex items-center gap-2 text-sm rounded px-1 py-0.5",
                                    blocked && "opacity-50"
                                  )}
                                  title={
                                    blocked
                                      ? "Not exposed for unauthenticated public export. Grant public_publish_collection or public_expose_fields on this field (and related collection for relation paths)."
                                      : undefined
                                  }
                                >
                                  <Checkbox
                                    checked={src.fields.includes(opt.key)}
                                    disabled={disableCheckbox}
                                    onCheckedChange={(c) => {
                                      if (disableCheckbox) return;
                                      toggleField(si, opt.key, c === true);
                                    }}
                                  />
                                  <span>{opt.label}</span>
                                </label>
                              );
                            })}
                          </div>
                        </div>
                      )}
                      <div>
                        <Label>Filters (optional)</Label>
                        <p className="text-xs text-muted-foreground mb-2">
                          Field must exist on the collection. Select and checkbox fields use typed controls; other
                          types accept text. Use commas for multiple values ($in).
                        </p>
                        <div className="space-y-2">
                          {src.filterRows.map((row, ri) => {
                            const filterField = row.field ? fieldByName(comp, row.field) : undefined;
                            let selectOpts: string[] =
                              filterField?.type === "select"
                                ? Array.from(
                                  new Set(parseSelectOptions(filterField.options ?? filterField.value))
                                )
                                : [];
                            if (
                              filterField?.type === "select" &&
                              row.value.trim() &&
                              !selectOpts.includes(row.value.trim())
                            ) {
                              selectOpts = [...selectOpts, row.value.trim()];
                            }

                            return (
                              <div key={ri} className="flex gap-2 flex-wrap items-center">
                                <Select
                                  value={row.field || "__empty__"}
                                  onValueChange={(v) => {
                                    const next = [...src.filterRows];
                                    next[ri] = {
                                      field: v === "__empty__" ? "" : v,
                                      value: ""
                                    };
                                    updateSource(si, { filterRows: next });
                                  }}
                                >
                                  <SelectTrigger className="w-[160px]">
                                    <SelectValue placeholder="Field" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="__empty__">—</SelectItem>
                                    {(comp?.fields || [])
                                      .filter((f) => f?.name)
                                      .map((f) => (
                                        <SelectItem key={f.name} value={f.name}>
                                          {f.label || f.name}
                                        </SelectItem>
                                      ))}
                                  </SelectContent>
                                </Select>
                                {filterField?.type === "checkbox" ? (
                                  <Select
                                    value={row.value || "__unset__"}
                                    onValueChange={(v) => {
                                      const next = [...src.filterRows];
                                      next[ri] = {
                                        ...next[ri],
                                        value: v === "__unset__" ? "" : v
                                      };
                                      updateSource(si, { filterRows: next });
                                    }}
                                  >
                                    <SelectTrigger className="flex-1 min-w-[140px]">
                                      <SelectValue placeholder="Value" />
                                    </SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="__unset__">—</SelectItem>
                                      <SelectItem value="true">True</SelectItem>
                                      <SelectItem value="false">False</SelectItem>
                                    </SelectContent>
                                  </Select>
                                ) : filterField?.type === "select" && selectOpts.length > 0 ? (
                                  <Select
                                    value={row.value || "__unset__"}
                                    onValueChange={(v) => {
                                      const next = [...src.filterRows];
                                      next[ri] = {
                                        ...next[ri],
                                        value: v === "__unset__" ? "" : v
                                      };
                                      updateSource(si, { filterRows: next });
                                    }}
                                  >
                                    <SelectTrigger className="flex-1 min-w-[140px]">
                                      <SelectValue placeholder="Value" />
                                    </SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="__unset__">—</SelectItem>
                                      {selectOpts.map((opt) => (
                                        <SelectItem key={opt} value={opt}>
                                          {opt}
                                        </SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                ) : (
                                  <Input
                                    className="flex-1 min-w-[120px]"
                                    placeholder="Value"
                                    value={row.value}
                                    onChange={(e) => {
                                      const next = [...src.filterRows];
                                      next[ri] = { ...next[ri], value: e.target.value };
                                      updateSource(si, { filterRows: next });
                                    }}
                                  />
                                )}
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  onClick={() => {
                                    const next = src.filterRows.filter((_, i) => i !== ri);
                                    updateSource(si, {
                                      filterRows: next.length ? next : [{ field: "", value: "" }]
                                    });
                                  }}
                                >
                                  ×
                                </Button>
                              </div>
                            );
                          })}
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() =>
                              updateSource(si, { filterRows: [...src.filterRows, { field: "", value: "" }] })
                            }
                          >
                            Add filter row
                          </Button>
                        </div>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Relation fields follow the same ACL as fields. Exposed relation values also require ACL visibility on the related collection/fields.
                      </p>
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            <div className="flex justify-end gap-2 pt-4 border-t">
              <Button type="button" variant="outline" onClick={closeDialog}>
                Cancel
              </Button>
              <Button type="button" onClick={save} disabled={saving || !title.trim()}>
                {saving ? "Saving…" : "Save"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog
        open={sortDialogOpen}
        onOpenChange={(open) => {
          if (!open) {
            setSortDialogOpen(false);
            clearSortDragState();
          }
        }}
      >
        <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Reorder sources</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Drag items to set the order of source blocks. This order matches the sequence of keys in the published JSON
            when keys are unique.
          </p>
          <div className="space-y-0 mt-2" onDragLeave={handleSortDragLeaveRow}>
            {sortRows.map((row, index) => {
              const comp = availableComponents.find((c) => c.slug === row.source.componentSlug.trim());
              const subtitle = row.source.componentSlug.trim()
                ? comp
                  ? `${comp.name} (${row.source.componentSlug})`
                  : row.source.componentSlug
                : "— no collection —";
              const keyLabel = row.source.key.trim() || "(unnamed key)";
              return (
                <div key={row.id} onDragOver={(e) => handleSortDragOver(e, index)} onDrop={(e) => handleSortDrop(e, index)}>
                  {sortDropIndex === index && (
                    <div className="h-0.5 bg-primary rounded-full my-0.5" aria-hidden />
                  )}
                  <div
                    draggable
                    onDragStart={(e) => handleSortDragStart(e, index)}
                    onDragEnd={handleSortDragEnd}
                    className={cn(
                      "flex items-center gap-2 rounded-md border bg-card px-2 py-2.5 mb-2 cursor-grab active:cursor-grabbing",
                      sortDragIndex === index && "opacity-60"
                    )}
                  >
                    <GripVertical className="w-4 h-4 shrink-0 text-muted-foreground" aria-hidden />
                    <div className="min-w-0 flex-1">
                      <div className="font-medium text-sm truncate">{keyLabel}</div>
                      <div className="text-xs text-muted-foreground truncate">{subtitle}</div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          <div className="flex justify-end gap-2 pt-2 border-t">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setSortDialogOpen(false);
                clearSortDragState();
              }}
            >
              Cancel
            </Button>
            <Button type="button" onClick={applySortSourcesOrder}>
              Apply order
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PublicJsonFeedsManager;
